import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { LoadingIndicator } from 'atoms';
import { useProvisioningFolders, usePandadocSync } from 'core/hooks';
import { PATHS } from 'router/config';

import * as S from './PandaDocProvisioning.styled';
import { COURSE_FILTER_OPTIONS } from './constants';

/** Screen «Список папок» — folders of signed PandaDoc contracts (§4.1). */
export const FoldersList = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const [course, setCourse] = useState('all');

  const {
    folders,
    loading,
    fetching,
    permissionDenied,
    refetch,
  } = useProvisioningFolders(course === 'all' ? undefined : course);

  // Manual PandaDoc re-sync: re-queries the folder list once the backend has
  // drained the details backlog, so freshly synced folders appear.
  const { triggerSync, syncing, detailsPending } = usePandadocSync(refetch);

  const openFolder = useCallback(
    (folderUuid: string) => {
      history.push(`${PATHS.debtorsPandadocProvisioning}/${folderUuid}`);
    },
    [history],
  );

  if (permissionDenied) {
    return (
      <S.EmptyState>{t('pages.pandadocProvisioning.noAccess')}</S.EmptyState>
    );
  }

  return (
    <>
      <S.HeaderRow>
        <S.PageTitle>{t('pages.pandadocProvisioning.list.title')}</S.PageTitle>
        <S.GhostButton type="button" disabled={fetching} onClick={refetch}>
          {t('pages.pandadocProvisioning.list.reload')}
        </S.GhostButton>
        <S.SyncButton type="button" disabled={syncing} onClick={triggerSync}>
          {syncing && <S.Spinner />}
          {!syncing && t('pages.pandadocProvisioning.sync.button')}
          {syncing && detailsPending !== null
            ? t('pages.pandadocProvisioning.sync.inProgress', {
                count: detailsPending,
              })
            : ''}
          {syncing && detailsPending === null
            ? t('pages.pandadocProvisioning.sync.checking')
            : ''}
        </S.SyncButton>
      </S.HeaderRow>
      <S.SubTitle>{t('pages.pandadocProvisioning.list.subtitle')}</S.SubTitle>

      <S.Controls>
        <S.ControlGroup>
          <S.ControlLabel>
            {t('pages.pandadocProvisioning.list.course')}
          </S.ControlLabel>
          <S.Select
            value={course}
            onChange={(event) => setCourse(event.target.value)}
          >
            {COURSE_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.value === 'all'
                  ? t('pages.pandadocProvisioning.list.courseAll')
                  : option.value}
              </option>
            ))}
          </S.Select>
        </S.ControlGroup>
      </S.Controls>

      {loading && !folders.length && (
        <S.CenterLoader>
          <LoadingIndicator />
        </S.CenterLoader>
      )}

      {!loading && !folders.length && (
        <S.EmptyState>
          {t('pages.pandadocProvisioning.list.empty')}
        </S.EmptyState>
      )}

      {Boolean(folders.length) && (
        <S.Card>
          <S.Table>
            <thead>
              <tr>
                <th>{t('pages.pandadocProvisioning.list.headerFolder')}</th>
                <th>{t('pages.pandadocProvisioning.list.headerCourse')}</th>
                <th>{t('pages.pandadocProvisioning.list.headerGroup')}</th>
                <th>{t('pages.pandadocProvisioning.list.headerStudents')}</th>
              </tr>
            </thead>
            <tbody>
              {folders.map((folder) => {
                const docs = folder.studentDocs ?? [];
                const provisioned = docs.filter(
                  (doc) => doc?.alreadyProvisioned,
                ).length;

                return (
                  <S.FolderRow
                    key={folder.folderUuid ?? folder.folderName ?? ''}
                    onClick={() => openFolder(folder.folderUuid ?? '')}
                  >
                    <td>
                      <strong>{folder.folderName || '—'}</strong>
                    </td>
                    <td>
                      {folder.detectedCourseName || (
                        <S.Badge variant="warn">
                          {t(
                            'pages.pandadocProvisioning.list.courseUndetected',
                          )}
                        </S.Badge>
                      )}
                    </td>
                    <td>
                      {folder.existingGroupId ? (
                        <S.Badge variant="ok">
                          {t('pages.pandadocProvisioning.list.groupExists')}
                        </S.Badge>
                      ) : (
                        <S.Badge variant="neutral">
                          {t('pages.pandadocProvisioning.list.groupMissing')}
                        </S.Badge>
                      )}
                    </td>
                    <td>
                      {docs.length}
                      {provisioned > 0
                        ? t('pages.pandadocProvisioning.list.provisioned', {
                            count: provisioned,
                          })
                        : ''}
                    </td>
                  </S.FolderRow>
                );
              })}
            </tbody>
          </S.Table>
        </S.Card>
      )}
    </>
  );
});
